import { isEmpty, isPlainObject, mapValues } from "lodash";
import moment from "moment";

import { DEFAULT_PAGE_SIZE, SELECT_COLUMN_ID } from "constants/Constants";
import {
  operandsOf,
  readEnvelope,
  wireParam,
  wireParamValue,
} from "components/NewAdvancedSearch/envelope";

export const defaultQueryParams = {
  search: "",
  sort_by: null,
  sort_dir: null,
  page: 1,
  per_page: DEFAULT_PAGE_SIZE,
};

export const defaultColumnPinning = {
  left: [SELECT_COLUMN_ID],
  right: [],
};

const MAX_PAGE_SIZE = 100;

export const getFlattenedFilters = filters => {
  return filters.reduce((accum, cur) => {
    if (cur.type === "grid") {
      // A grid is a group of independent boolean flags, so each component
      // exports as its own checkbox param. Deriving the type from the grid
      // itself keeps CSV export working without a `componentProps` key.
      const comp = cur.components.map(c => ({ ...c, type: "checkbox" }));
      return [...accum, ...comp];
    }
    return [...accum, cur];
  }, []);
};

// Builds the advanced-search filter descriptors for a model's custom fields.
// The per-field config (from getCustomFieldConfigsForModel) carries the type and
// options the filter editors need — the plain field list only has names — so a
// select field gets its choices, a date field a date picker, etc. `groups` are
// the custom-field groups a table hook assembles, each with a `configs` array;
// `existingTitles` skips fields already exposed as native filters.
export const buildCustomFieldFilters = (groups = [], existingTitles = []) =>
  groups.flatMap(group =>
    (group.configs ?? [])
      .filter(field => !existingTitles.includes(field.label))
      .map(field => ({
        key: `search:custom:${field.model_type}:${field.label}`,
        title: field.label,
        type: field.field_type,
        model: field.model_type,
        options: field.field_options,
        isCustomField: true,
      }))
  );

export const getFilterParams = async (queryParams, filters) => {
  if (isEmpty(queryParams) || !filters.length) return {};
  const presentEntries = Object.entries(queryParams)
    .filter(([, value]) => value != null && value !== "")
    .map(([key, value]) => [
      key,
      Array.isArray(value) ? value.join(",") : value,
    ]);
  const discriminatedParams = presentEntries.reduce(
    (obj, [key, value]) => {
      const type = key.startsWith("search:custom:") ? "custom" : "regular";
      return {
        ...obj,
        [type]: { ...obj[type], [key]: value },
      };
    },
    { regular: {}, custom: {} }
  );
  const customFilters = Object.keys(discriminatedParams.custom).map(
    (key, idx) => {
      const parts = key.split(":");
      const hasUuid = parts.length > 4 && /^\d+$/.test(parts[parts.length - 1]);
      const selectValue = hasUuid ? parts.slice(0, -1).join(":") : key;
      const uuid = hasUuid ? parseInt(parts[parts.length - 1], 10) : idx;
      const filter = filters.find(filter => filter.key === selectValue);
      const model = filter ? filter.model : null;
      const value = discriminatedParams.custom[key];
      const label = filter ? filter.title : undefined;
      return { selectValue, inputValue: value, label, uuid, model };
    }
  );
  const filterParams = await generateFilterParamsFromQueryParams(
    discriminatedParams.regular,
    filters
  );
  return { filterParams, customFilters };
};

export const getListFilters = (queryParams = {}, filters = []) => {
  if (isEmpty(queryParams) || isEmpty(filters)) return [];
  const flattenedFilters = getFlattenedFilters(filters);

  const listFilterConfigs = flattenedFilters.filter(
    f => f.type === "listFilter"
  );
  const listFilters = [];

  Object.keys(queryParams).forEach(key => {
    // Match pattern: search:<filterKey>:<uuid>
    const match = key.match(/^search:([^:]+):(.+)$/);
    if (!match) return;

    const [, filterKey, uuid] = match;
    const config = listFilterConfigs.find(c => c.key === filterKey);
    if (!config) return;

    try {
      const parsed = JSON.parse(queryParams[key]);
      listFilters.push({
        key: filterKey,
        title: config.title,
        operationType: parsed.operationType,
        values: parsed.values,
        uuid,
      });
    } catch (e) {
      // Invalid JSON, skip
    }
  });

  return listFilters;
};

export const getListFiltersState = (queryParams = {}, filters = []) => {
  const flattenedFilters = getFlattenedFilters(filters);
  return getListFilters(queryParams, flattenedFilters).reduce((acc, item) => {
    const config = flattenedFilters.find(
      f => f.key === item.key && f.type === "listFilter"
    );
    const stateKey = config?.stateKey || `${item.key}Filters`;
    if (!acc[stateKey]) acc[stateKey] = [];
    acc[stateKey].push({
      operationType: item.operationType,
      values: item.values,
      uuid: Number(item.uuid),
    });
    return acc;
  }, {});
};

export const sortToTableState = queryParams => {
  const id = queryParams?.sort_by;
  const dir = queryParams?.sort_dir;

  if (id && dir) {
    return [{ id, desc: dir === "desc" }];
  }

  return [];
};

export const sortToQueryParams = tableState => {
  if (!tableState.length) {
    return { sort_by: null, sort_dir: null };
  }
  return {
    sort_by: tableState[0]?.id,
    sort_dir: tableState[0]?.desc ? "desc" : "asc",
  };
};

export const getColumnVisibilityMap = ({ visibleColumns, columns }) => {
  return columns
    .filter(col => typeof col.header === "string")
    .reduce(
      (acc, curr) => ({
        ...acc,
        [curr.id]:
          visibleColumns.includes(curr.id) ||
          visibleColumns.includes(curr.accessorKey),
      }),
      {}
    );
};

export const getInitialSort = (columns = [], sort = {}) => {
  if (!sort?.sort_by) return {};
  const column = columns.find(
    col => col.id === sort.sort_by || col.accessorKey === sort.sort_by
  );
  if (!column) return {};
  return {
    sort_by: column.id,
    sort_dir: sort.sort_dir === "desc" ? "desc" : "asc",
  };
};

export const isActionsColumn = col =>
  col?.id?.toLowerCase() === "actions" || col?.accessorKey === "actions";

export const buildColumnPinning = (freezedColumns = [], columns = []) => {
  const hasSelectColumn = columns.some(col => col.id === SELECT_COLUMN_ID);
  const actionsId = columns.find(isActionsColumn)?.id;
  const isFrozenActions =
    actionsId != null && freezedColumns.includes(actionsId);
  const left = isFrozenActions
    ? freezedColumns.filter(col => col !== actionsId)
    : freezedColumns;
  const right = isFrozenActions ? [actionsId] : [];

  return {
    left: hasSelectColumn ? [SELECT_COLUMN_ID, ...left] : left,
    right,
  };
};

export const paginationToTableState = queryParams => {
  const page = parseInt(queryParams.page);
  const per_page = parseInt(queryParams.per_page);

  if (isNaN(page) || isNaN(per_page)) {
    return {
      pageIndex: defaultQueryParams.page - 1,
      pageSize: defaultQueryParams.per_page,
    };
  }

  return {
    pageIndex: page - 1,
    pageSize: per_page > MAX_PAGE_SIZE ? MAX_PAGE_SIZE : per_page,
  };
};

export const paginationToQueryParams = tableState => {
  return {
    page: `${tableState.pageIndex + 1}`,
    per_page: `${tableState.pageSize}`,
  };
};

export const getInitialColumnOrder = (visibleColumns = [], columns = []) => {
  if (!columns || !columns.length) return [];
  if (!visibleColumns || !visibleColumns.length) {
    return columns
      .filter(col => typeof col.header === "string")
      ?.map(col => col.id);
  }

  const actionsColumnIndex = visibleColumns.indexOf("Actions");
  if (actionsColumnIndex === -1) return visibleColumns;
  // we need to move Actions column to the end of the array
  const columnsWithoutActions = visibleColumns.filter(col => col !== "Actions");
  return [
    ...columnsWithoutActions.slice(0, actionsColumnIndex),

    ...columnsWithoutActions.slice(actionsColumnIndex),
    "Actions",
  ];
};

const parseJsonChoiceList = value => {
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [parsed];
  } catch {
    return [];
  }
};

export const generateFilterParamsFromQueryParams = async (
  queryParams,
  filters
) => {
  let filterParams = {};
  for (const [key, value] of Object.entries(queryParams)) {
    const filter = filters.find(filter => key.startsWith(filter.key));

    if (!filter) {
      continue;
    }
    const { title, type, data, queryFn, props } = filter;
    let param = null;
    switch (type) {
      case "dateRange":
        // The panel emits the envelope on the bare key, which carries no
        // :from / :to suffix for the sub-key below to pick up.
        param = readEnvelope(value)
          ? wireParam(value)
          : {
              ...filterParams[title],
              [key.replace(`${filter.key}:`, "")]: moment(value),
            };
        break;
      case "select":
      case "ruvixxSelect":
        // A list of options can only say "any of these", so any other
        // operator goes to the export verbatim instead of being flattened.
        if (readEnvelope(value)?.some(clause => clause.op !== "in")) {
          param = wireParam(value);
          break;
        }
        param = await (async () => {
          // The panel emits the { op, value } envelope now; the CSV and bare
          // JSON-list reads stay for bookmarked URLs and saved filters.
          const clauses = readEnvelope(value);
          const rawIds = clauses?.length
            ? clauses.flatMap(operandsOf)
            : filter.json
              ? parseJsonChoiceList(value)
              : value.split(",");
          const ids = rawIds.map(id => {
            if (
              typeof id === "string" &&
              !isNaN(+id) &&
              !["caller_id", "contact_number", "machine_id"].includes(
                filter.key
              )
            ) {
              id = +id;
            }
            if (["true", "false"].includes(id)) {
              id = id === "true";
            }
            return id;
          });

          if (!data && queryFn) {
            const data = await queryFn({ model_id: ids });

            if (props?.includeNoneOption) {
              data.unshift({
                id: "Null",
                key: "Null",
                text: "Null",
                value: "Null",
              });
            }
            const filtered = data
              .filter(({ id }) => ids.includes(id))
              .map(({ id, full_name, name, text }) => ({
                key: id,
                text: full_name || name || text || "",
                value: id,
              }));
            return filtered;
          } else {
            return ids.map(id => {
              const item = data.find(({ id: itemId }) => itemId === id);
              return { key: id, text: item ? item.name : "", value: id };
            });
          }
        })();
        break;
      case "ruvixxToggle":
        param = String(value) === "true";
        break;
      case "range":
        param = readEnvelope(value)
          ? wireParam(value)
          : {
              ...filterParams[title],
              [key.replace(`${filter.key}:`, "")]: value,
            };
        break;
      default:
        param = value;
    }
    if (isPlainObject(param) && wireParamValue(param) === null) {
      param = mapValues(param, value => {
        if (typeof value === "string" && !isNaN(+value)) {
          value = +value; // cast string to number
        }
        return value;
      });
    }
    filterParams = {
      ...filterParams,
      [title]: param,
    };
  }
  return filterParams;
};

export const generateQueryParamsFromFilterParams = (filterParams, filters) => {
  return Object.keys(filterParams).reduce((obj, cur) => {
    const filter = filters.find(({ title }) => title === cur);
    if (!filter) {
      return obj;
    }
    const param = ((key, value, type) => {
      switch (type) {
        case "dateRange":
          return ["from", "to"].reduce(
            (filter, cur) => ({
              ...filter,
              ...(value[cur] && {
                [`${key}:${cur}`]: value[cur].format("YYYY-MM-DD"),
              }),
            }),
            {}
          );
        case "select":
        case "ruvixxSelect": {
          const raw = Array.isArray(value) ? value.map(o => o.value) : value;
          if (filter.json) {
            // Custom-table choices may contain commas, so JSON-encode the
            // selection rather than rely on the qs comma-join other tables use.
            return { [key]: JSON.stringify(Array.isArray(raw) ? raw : [raw]) };
          }
          return { [key]: raw };
        }
        case "range":
          return ["type", "start", "end"].reduce(
            (filter, cur) => ({
              ...filter,
              ...(value[cur] != null && {
                [`${key}:${cur}`]: value[cur],
              }),
            }),
            {}
          );
        default:
          return {
            [key]: value,
          };
      }
    })(filter.key, filterParams[cur], filter.type);
    return { ...obj, ...param };
  }, {});
};

export const generateQueryParamsFromCustomFilters = customFilters => {
  let cf = customFilters
    .filter(({ selectValue }) => selectValue)
    .reduce((obj, { selectValue, inputValue, uuid }) => {
      let value = inputValue;
      if (moment.isMoment(inputValue)) {
        value = inputValue.utc().format("YYYY-MM-DD");
      } else if (
        inputValue &&
        typeof inputValue === "object" &&
        !Array.isArray(inputValue) &&
        inputValue.op
      ) {
        value = JSON.stringify(inputValue);
      }
      return {
        ...obj,
        [`${selectValue}:${uuid}`]: value === null ? "search_on_key" : value,
      };
    }, {});
  return cf;
};

export const generateQueryParamsFromTagFilters = tagsFilters => {
  const tags = tagsFilters
    .filter(({ values }) => values.length > 0)
    .map(({ operationType: op, values }) => {
      op = op === "excludes" ? "-" : "";
      const joinedValues = values.join(",");
      return op + joinedValues;
    })
    .join(";");
  return { tags };
};

export const generateQueryParamsFromListFilters = (
  listFiltersState,
  filters
) => {
  if (!listFiltersState || !filters) return {};

  const queryParams = {};

  // Find all listFilter type filters
  const listFilterConfigs = filters.filter(f => f.type === "listFilter");

  listFilterConfigs.forEach(config => {
    const stateKey = config.stateKey || `${config.key}Filters`;
    const filterList = listFiltersState[stateKey];

    if (filterList && filterList.length) {
      filterList.forEach((filter, _index) => {
        if (filter?.values?.length && filter?.uuid != null) {
          queryParams[`search:${config.key}:${filter.uuid}`] = JSON.stringify({
            operationType: filter.operationType,
            values: filter.values,
          });
        }
      });
    }
  });

  return queryParams;
};

const tagGroup = (operationType, values, idx) => ({
  operationType,
  label: operationType === "excludes" ? "Exclude tags: " : "Tags: ",
  values,
  uuid: idx,
});

// The search panel sends tags as a clause array; a bookmarked URL or a saved
// filter still carries the `1,2;-3` string. Both resolve to the same groups,
// which the download builder and the chip row read.
export const getTagFilters = (tags = "") => {
  if (!tags) return [];

  const clauses = readEnvelope(tags);
  if (clauses) {
    return clauses.map((clause, idx) =>
      tagGroup(
        clause.op === "not_in" ? "excludes" : "contains",
        operandsOf(clause).map(Number),
        idx
      )
    );
  }

  return tags.split(";").map((str, idx) => {
    const excludes = str.indexOf("-") === 0;
    str = str.slice(+excludes);
    return tagGroup(
      excludes ? "excludes" : "contains",
      str.split(",").map(Number),
      idx
    );
  });
};

export function applyTagFilters(tagsFilters = []) {
  if (tagsFilters?.length) {
    const queryTagFilters = tagsFilters.reduce(
      (acc, curr) =>
        curr.values.length
          ? {
              ...acc,
              [`search:tags:${curr.uuid}`]: JSON.stringify({
                operationType: curr.operationType,
                tags: curr.values,
              }),
            }
          : acc,
      {}
    );
    return queryTagFilters;
  }
  return {};
}

const normalizeWhitespace = text => text.replace(/\s+/g, " ").trim();

/**
 * Reads the text a cell shows on screen. List cells render one `<li>` per
 * value (the ScrollColumn contract), so their values are joined with ", "
 * after dropping the separator comma ScrollColumn draws between items.
 */
export const getCellDisplayText = element => {
  const items = element.querySelectorAll("li");
  if (!items.length) {
    return normalizeWhitespace(element.textContent);
  }
  return Array.from(items)
    .map(item => normalizeWhitespace(item.textContent).replace(/,$/, ""))
    .filter(Boolean)
    .join(", ");
};
